import { useState, useEffect } from "react";
import { X, Sparkles, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";

const newsletterSchema = z.object({
  email: z.string().trim().email().max(255),
});

const NewsletterPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState(""); // Honeypot field for bot detection
  const [formTimestamp, setFormTimestamp] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Check if user has already dismissed or subscribed
    const hasInteracted = localStorage.getItem("newsletter-popup-interacted");
    
    if (!hasInteracted) {
      // Show popup after 15 seconds of browsing
      const timer = setTimeout(() => {
        setIsVisible(true);
        setFormTimestamp(Date.now());
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem("newsletter-popup-interacted", "dismissed");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const parsed = newsletterSchema.safeParse({ email });
    if (!parsed.success) {
      toast({
        title: "Email inválido",
        description: "Por favor ingresa un correo electrónico válido.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    const normalizedEmail = parsed.data.email.trim().toLowerCase();

    const { error } = await supabase.functions.invoke("send-newsletter", {
      body: { 
        email: normalizedEmail,
        honeypot, // Include honeypot for server-side bot detection
        timestamp: formTimestamp, // Include timestamp for timing check
      },
    });

    if (error) {
      toast({
        title: "No se pudo suscribir",
        description: "Intenta nuevamente en unos segundos.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }
    
    toast({
      title: "¡Suscripción exitosa!",
      description: "Recibirás tu horóscopo mensual en tu correo.",
    });
    
    localStorage.setItem("newsletter-popup-interacted", "subscribed");
    setIsVisible(false);
    setIsSubmitting(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-5 duration-500">
      <div className="relative max-w-sm w-full gradient-mystical rounded-2xl border border-primary/30 shadow-2xl overflow-hidden">
        {/* Decorative glow effect */}
        <div className="absolute -top-20 -right-20 w-40 h-40 bg-primary/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-secondary/20 rounded-full blur-2xl" />
        
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 p-1.5 rounded-full bg-background/50 hover:bg-background/80 transition-colors z-10"
          aria-label="Cerrar"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>

        <div className="relative p-6">
          {/* Header with icon */}
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 rounded-xl bg-primary/20 border border-primary/30">
              <Sparkles className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h3 className="font-bold text-lg text-foreground">
                Suscríbete a nuestro boletín
              </h3>
              <p className="text-xs text-muted-foreground">
                Novedades en tu correo
              </p>
            </div>
          </div>

          {/* Description */}
          <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
            Recibe las últimas novedades sobre horóscopo, tarot y astrología.
            <span className="text-primary font-medium"> ¡Gratis!</span>
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Honeypot field - hidden from users, bots will fill it */}
            <input
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
            />
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 bg-background/60 border-border/50 focus:border-primary/50"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  Suscribiendo...
                </span>
              ) : (
                "Suscribirme Gratis"
              )}
            </Button>
          </form>

          {/* Privacy note */}
          <p className="text-[10px] text-muted-foreground/70 text-center mt-3">
            Sin spam. Cancela cuando quieras.
          </p>
        </div>
      </div>
    </div>
  );
};

export default NewsletterPopup;
